import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { XIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
import type { RecipeSample } from './salesData';
interface RecipeViewerProps {
  sample: RecipeSample;
  onClose: () => void;
}
const ZOOM_LEVELS = [1, 1.5, 2, 3];

/**
 * Visor de página completa: zoom, desplazamiento para leer, Cerrar visible,
 * cierre con Escape y devolución del foco al control que lo abrió.
 */
export function RecipeViewer({
  sample,
  onClose
}: RecipeViewerProps) {
  const [zoomIdx, setZoomIdx] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const zoom = ZOOM_LEVELS[zoomIdx];
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [onClose]);
  const tool = 'flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-out hover:bg-white/20 focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 disabled:cursor-not-allowed disabled:text-white/30 disabled:hover:bg-white/10';
  return createPortal(<motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className="fixed inset-0 z-[10000] flex flex-col bg-charcoal/95" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-217" data-mp="1l3602dm64wov">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="recipe-viewer-title" className="flex h-full w-full flex-col" style={{
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-218" data-mp="28sihu1vkad2s">
        <div className="flex shrink-0 items-center gap-2 border-b border-white/10 px-3 py-2.5 sm:px-5" data-id="element-219" data-mp="2whtcr2q8dd90">
          <div className="min-w-0 flex-1" data-id="element-220" data-mp="2rqb9u94nd2le">
            <p className="font-display text-[0.6rem] font-bold uppercase tracking-editorial text-gold" data-id="element-221" data-mp="2rp9z65rjmi47">
              {sample.tomo}
            </p>
            <h2 id="recipe-viewer-title" className="truncate font-display text-[0.9rem] font-bold leading-tight text-white sm:text-[1rem]" data-id="element-222" data-mp="340aprixbfa6q">
              {sample.name}
            </h2>
          </div>
          <button type="button" onClick={() => setZoomIdx(z => Math.max(0, z - 1))} disabled={zoomIdx === 0} aria-label="Alejar" className={tool} data-id="element-223" data-mp="3d322dm1xgbzy">
            <ZoomOutIcon className="h-5 w-5" aria-hidden strokeWidth={2.25} data-id="element-224" data-mp="3mxlpt5qru7n8" />
          </button>
          <span className="w-11 text-center font-display text-[0.72rem] font-bold tabular-nums text-white/80" data-id="element-225" data-mp="3rshpms291zaw">
            {Math.round(zoom * 100)}%
          </span>
          <button type="button" onClick={() => setZoomIdx(z => Math.min(ZOOM_LEVELS.length - 1, z + 1))} disabled={zoomIdx === ZOOM_LEVELS.length - 1} aria-label="Acercar" className={tool} data-id="element-226" data-mp="4n07tbzqlp64">
            <ZoomInIcon className="h-5 w-5" aria-hidden strokeWidth={2.25} data-id="element-227" data-mp="3o23zagla5jot" />
          </button>
          <button ref={closeRef} type="button" onClick={onClose} className="ml-1 inline-flex h-10 items-center gap-1.5 rounded-full bg-gold px-4 font-display text-[0.78rem] font-bold uppercase tracking-[0.04em] text-charcoal transition-colors duration-150 ease-out hover:bg-[#F5B900] focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50" data-id="element-228" data-mp="5nl6cohtnfyw">
            <XIcon className="h-4 w-4" aria-hidden strokeWidth={3} data-id="element-229" data-mp="3j3ak8jqchkmj" />
            Cerrar
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto overscroll-contain p-3 sm:p-6" data-id="element-230" data-mp="3e2mvk4pl9fvh">
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="mx-auto origin-top" style={{
          width: `${zoom * 100}%`,
          maxWidth: `${860 * zoom}px`
        }} data-id="element-231" data-mp="ybqsnfoixd0u">
            <img src={sample.image} alt={`Página completa de la receta ${sample.name}`} width={sample.width} height={sample.height} decoding="async" className="block h-auto w-full rounded-lg bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)]" data-id="element-232" data-mp="2gaybyxubwxrr" />
          </motion.div>
        </div>
      </div>
    </motion.div>, document.body);
}